/**
 * React Query hooks for Platform Capabilities configuration (REQ-CAP-002).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import {
  fetchAgentCapabilities,
  fetchCapabilitiesCatalog,
  fetchHiveCapabilities,
  putHiveDecisions,
  putTenantPolicy,
} from './api';
import type { HiveCapabilityDecisionInput, TenantPolicyInput } from './types';

export const capabilityKeys = {
  all: ['capabilities'] as const,
  catalog: (tenantId: string | undefined) =>
    [...capabilityKeys.all, 'catalog', tenantId] as const,
  agent: (tenantId: string | undefined, agentId: string | undefined) =>
    [...capabilityKeys.all, 'agent', tenantId, agentId] as const,
  hive: (tenantId: string | undefined, hiveId: string | undefined) =>
    [...capabilityKeys.all, 'hive', tenantId, hiveId] as const,
};

export function useCapabilitiesCatalog() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: capabilityKeys.catalog(tenantId),
    queryFn: () => fetchCapabilitiesCatalog(tenantId as string),
    enabled: !!tenantId,
  });
}

export function useAgentCapabilities(agentId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: capabilityKeys.agent(tenantId, agentId),
    queryFn: () => fetchAgentCapabilities(tenantId as string, agentId as string),
    enabled: !!tenantId && !!agentId,
  });
}

export function useHiveCapabilities(hiveId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: capabilityKeys.hive(tenantId, hiveId),
    queryFn: () => fetchHiveCapabilities(tenantId as string, hiveId as string),
    enabled: !!tenantId && !!hiveId,
  });
}

export function useSetHiveCapabilities(hiveId: string | undefined) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (decisions: HiveCapabilityDecisionInput[]) =>
      putHiveDecisions(tenantId as string, hiveId as string, decisions),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}

export function useSetTenantPolicy() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, input }: { slug: string; input: TenantPolicyInput }) =>
      putTenantPolicy(tenantId as string, slug, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
    },
  });
}
